import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, ShoppingCart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, HAS_API_URL } from '../config/api';

const POLL_MS = 2500;
const MAX_VISIBLE = 3;

function itemLabel(item) {
  const label = typeof item?.label === 'string' ? item.label.trim() : '';
  if (label) return label;
  const category = typeof item?.category === 'string' ? item.category.trim() : '';
  return category || 'Detected item';
}

function itemThumbUrl(item) {
  if (typeof item?.cropUrl === 'string' && item.cropUrl.trim()) return item.cropUrl.trim();
  if (typeof item?.cropImageUrl === 'string' && item.cropImageUrl.trim()) return item.cropImageUrl.trim();
  return '';
}

function ProjectShopLookSection({ projectId, generatedImageUrl, onOpenShop, onItemsReady }) {
  const { user } = useAuth();
  const fetchRequestIdRef = useRef(0);
  const [items, setItems] = useState([]);
  const [prepStatus, setPrepStatus] = useState('');

  const fetchShopItems = useCallback(async () => {
    if (!user || !projectId || !HAS_API_URL) return null;
    const idToken = await user.getIdToken(true);
    const res = await fetch(
      `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/shop-items`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      },
    );
    const text = await res.text();
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      throw new Error(
        (body && typeof body.error === 'string' && body.error) || `Request failed (${res.status}).`,
      );
    }
    return body;
  }, [user, projectId]);

  const loadShopItems = useCallback(async () => {
    if (!user || !projectId || !generatedImageUrl) return;
    const myRequestId = ++fetchRequestIdRef.current;
    try {
      const body = await fetchShopItems();
      if (myRequestId !== fetchRequestIdRef.current) return;
      const status = typeof body?.shopPrepStatus === 'string' ? body.shopPrepStatus : 'processing';
      setPrepStatus(status);
      const nextItems = status === 'ready' && Array.isArray(body?.items) ? body.items : [];
      setItems(nextItems);
      onItemsReady?.({ count: nextItems.length, items: nextItems });
    } catch {
      if (myRequestId !== fetchRequestIdRef.current) return;
      setItems([]);
      onItemsReady?.({ count: 0, items: [] });
    }
  }, [user, projectId, generatedImageUrl, fetchShopItems, onItemsReady]);

  useEffect(() => {
    if (!user || !projectId || !generatedImageUrl) return undefined;
    fetchRequestIdRef.current += 1;
    loadShopItems();
    return () => {
      fetchRequestIdRef.current += 1;
    };
  }, [user, projectId, generatedImageUrl, loadShopItems]);

  useEffect(() => {
    if (prepStatus !== 'processing') return undefined;
    const id = window.setInterval(() => {
      loadShopItems();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [prepStatus, loadShopItems]);

  const visible = items.slice(0, MAX_VISIBLE);
  if (visible.length === 0) return null;

  return (
    <section className="project-result-shop-section" aria-label="Shop your new design">
      <div className="project-result-shop-section-header">
        <button
          type="button"
          className="project-result-shop-quick-action"
          onClick={onOpenShop}
          aria-label="Shop this look"
        >
          <ShoppingCart size={17} strokeWidth={2} aria-hidden />
        </button>
        <div className="project-result-shop-section-titles">
          <h2>
            <span className="project-result-shop-section-heading-accent">Shop Your New Design</span>
          </h2>
          <p className="project-result-shop-section-subtitle">
            Furniture & decor detected in this design
          </p>
        </div>
      </div>
      <div className="project-result-shop-body">
        <div className="project-result-shop-actions-col">
          <button type="button" className="project-result-shop-view-all" onClick={onOpenShop}>
            Shop Items
            <ChevronRight size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="project-result-shop-grid">
          {visible.map((item, idx) => {
            const thumb = itemThumbUrl(item);
            const category = typeof item?.category === 'string' ? item.category.trim() : '';
            const key =
              typeof item?.itemId === 'string'
                ? item.itemId
                : typeof item?.id === 'string'
                  ? item.id
                  : `shop-preview-${idx}`;
            return (
              <button
                key={key}
                type="button"
                className="project-result-shop-card"
                onClick={onOpenShop}
              >
                <div className="project-result-shop-card-thumb">
                  {thumb ? (
                    <img src={thumb} alt="" loading="lazy" decoding="async" />
                  ) : null}
                </div>
                <div className="project-result-shop-card-body">
                  <p className="project-result-shop-card-title">{itemLabel(item)}</p>
                  {category ? (
                    <span className="project-result-shop-card-category">{category}</span>
                  ) : null}
                </div>
                <span className="project-result-shop-card-arrow" aria-hidden>
                  <ChevronRight size={15} strokeWidth={2} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ProjectShopLookSection;



